import type { KeyboardEvent } from 'react';
import { Avatar, Column, Divider, Row, Typography } from '@tempmee/react';
import { ICandidateListViewEntity } from '~/domain/PermanentJobs';
import { pluralize } from '~/utils/String';
import { POSTING_DETAILS_COPY } from '../../postingDetailsRules';

interface ICandidateRowProps {
  candidate: ICandidateListViewEntity;
  /** The professional is one of the office's favorites: the avatar gets the favorite ring. */
  isFavorite?: boolean;
  onOpen: (candidate: ICandidateListViewEntity) => void;
}

/**
 * One applicant in a Candidates section — the Figma's `Molecule/Slot/Applicant`: the
 * avatar (ringed for a favorite), the name, years of experience and when they applied.
 * There is no shortlist control and no inline action; the row opens the candidate.
 */
export const CandidateRow = ({ candidate, isFavorite = false, onOpen }: ICandidateRowProps) => {
  const { appliedAt, professional, yearsOfExperience } = candidate;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen(candidate);
    }
  };

  return (
    <Row
      align="center"
      gap={3}
      py={1}
      role="button"
      tabIndex={0}
      className="w-full cursor-pointer"
      data-cy="pj-candidate-list-item"
      onClick={() => onOpen(candidate)}
      onKeyDown={handleKeyDown}
    >
      <Avatar
        size="2xl"
        src={professional.picture || undefined}
        alt={`${professional.name} profile picture`}
        isFavorite={isFavorite}
      />
      <Column gap={0} className="min-w-0 flex-1">
        <Typography variant="bodyLarge" as="p" numberOfLines={1} className="text-text-primary">
          {professional.name}
        </Typography>
        <Row align="center" gap={1}>
          <Typography variant="body1" as="span" className="text-text-secondary">
            {`${yearsOfExperience} ${pluralize(POSTING_DETAILS_COPY.year, yearsOfExperience)}`}
          </Typography>
          {appliedAt ? (
            <>
              <Divider orientation="vertical" flexItem />
              <Typography variant="body2" as="span" className="text-text-secondary">
                <Typography variant="body2" as="span" bold className="text-text-secondary">
                  {POSTING_DETAILS_COPY.applied}
                </Typography>{' '}
                {appliedAt}
              </Typography>
            </>
          ) : null}
        </Row>
      </Column>
    </Row>
  );
};
